import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import warning from "../../resources/icons/red-warning.svg";
import BlueWarning from "../../resources/icons/blue-warning.svg";
import { AlertTriangle } from "lucide-react";

export default function VerificationApprovalModal({
  isModalOpen,
  toggleModal,
  actionType,
  handleConfirmAction,
  issuerComments,
  setIssuerComments,
  isLegacy = false,
  matchingVCs = [],
  vcStatus,
}) {
  function closeModal() {
    toggleModal(false);
  }

  if (!actionType) {
    return null;
  }

  const getConfirmationMessage = () => {
    if (actionType === "Approved" && isLegacy && matchingVCs.length === 0) {
      return "No matching credentials found, are you sure you want to approve the request?";
    } else if (actionType === "Approved") {
      return "Are you sure you want to approve the request?";
    } else {
      return "Are you sure you want to reject the request?";
    }
  };

  const isConfirmDisabled = () => {
    return !issuerComments || !issuerComments.trim();
  };

  return (
    <div 
        role="button"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") e.stopPropagation();
    }}
    >
      <Transition appear show={isModalOpen ? true : false} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25 backdrop-blur-md" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative">
                  <button
                    type="button"
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={closeModal}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>
                  <Dialog.Title
                    as="h3"
                    className="text-lg flex justify-center font-medium leading-6 text-gray-900"
                  >
                    <img
                      src={
                        actionType === "Approved"
                          ? BlueWarning
                          : actionType === "Rejected"
                          ? warning
                          : warning
                      }
                      alt="warning"
                    />
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-lg font-medium flex justify-center">
                      {actionType === "Approved" ? "Approve" : "Reject"}
                    </p>
                    {actionType !== "Approved" && (
                      <p className="text-sm text-gray-500 mt-2 flex justify-center">
                        {getConfirmationMessage()}
                      </p>
                    )}
                    {vcStatus === "Revoked" && actionType === "Approved" && (
                      <div className="p-3 bg-yellow-50 border-yellow-200 rounded-lg my-3">
                        <div className="flex items-center">
                          <div className="mr-3 text-cfIssuedCredentialTemplateCardStatusUnderReviewBG">
                            <AlertTriangle className="h-5 w-5" />
                          </div>
                          <p className="text-sm font-medium text-cfIssuedCredentialTemplateCardStatusUnderReviewBG">
                            This credential has been revoked. Are you sure you
                            want to approve the request?
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="mt-3">
                      <label
                        htmlFor="issuerComments"
                        className="block text-sm font-medium text-gray-700 mb-0.5"
                      >
                        {actionType === "Approved"
                          ? "Please add a comment to approve:"
                          : "Please provide a reason for rejection:"}
                      </label>
                      <textarea
                        id="issuerComments"
                        value={issuerComments}
                        onChange={(e) => setIssuerComments(e.target.value)}
                        className={`w-full px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 ${
                          actionType === "Approved"
                            ? "focus:ring-blue-500"
                            : "focus:ring-red-500"
                        }`}
                        rows={3}
                        placeholder={
                          actionType === "Approved"
                            ? "Enter approval comment..."
                            : "Enter rejection reason..."
                        }
                        style={{ fontSize: "0.875rem" }}
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-center gap-4">
                    <button
                      data-modal-hide="popup-modal"
                      type="button"
                      onClick={closeModal}
                      className="py-2.5 px-5 ms-3 text-sm hover:bg-gray-200 font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      data-modal-hide="popup-modal"
                      type="button"
                      onClick={handleConfirmAction}
                      className={`text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center transition-colors ${
                        isConfirmDisabled()
                          ? actionType === "Approved"
                            ? "bg-blue-300 cursor-not-allowed opacity-70"
                            : "bg-red-300 cursor-not-allowed opacity-70"
                          : actionType === "Approved"
                          ? "bg-[#3538CD] hover:bg-[#2a2da9]"
                          : "bg-[#D94D40] hover:bg-[#b84035]"
                      }`}
                      disabled={isConfirmDisabled()}
                    >
                      {actionType === "Approved" ? "Approve" : "Reject"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
